import { useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useQuery } from "react-query";
import useUser from "../../providers/user/user_provider";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import { getOrganizations } from "../../services/organizations/organizations";
import { Organization } from "../../views/organizations/model/organization";
import { purple } from "../../theme/colors";

// Sentinel option so "All organisations" is an explicit, bound value in the
// dropdown (id 0 never collides with a real org id) instead of an empty
// field that could read as "nothing selected yet".
const ALL_ORGS_OPTION: Organization = {
  id: 0,
  orgCode: "",
  name: "All Organisations",
  status: "active",
  tier: null,
  isPlatform: false,
  activeModuleCount: 0,
};

/**
 * Org-scoping filter for the Client Mapping / Manage Roles & Access list
 * pages, and the org a new Client is created in. Gated on actual
 * Platform-org membership (isPlatformOrg), not the super_admin role flag —
 * a "Super Admin" role homed in a tenant org is full power within that
 * tenant only, so it belongs to exactly one org and never needs to switch,
 * same as any other org admin. Backed by the shared `useSelectedOrgStore`,
 * so a selection made on one page is remembered when navigating to another.
 *
 * `lockToOwnOrg` (opt-in) makes a tenant (non-platform-org) user get a
 * visible, disabled field pinned to their own org instead of the component
 * rendering nothing — every list screen needs this just to show a tenant
 * user's org name.
 *
 * For a platform admin, `lockToOwnOrg` alone changes nothing: they always
 * get the full editable dropdown, defaulting to "All organisations" (no
 * org filter) same as without the prop. `platformDefaultsToOwnOrg` (opt-in,
 * used by create/modify forms where a concrete org must be picked) makes a
 * platform admin's dropdown default to their own org instead — list/filter
 * screens should leave this off so a platform admin's global view isn't
 * silently scoped down to just their own org's (possibly empty) data.
 */
export default function OrgSelector({
  label = "Organisation",
  fullWidth = false,
  lockToOwnOrg = false,
  platformDefaultsToOwnOrg = false,
}: {
  label?: string;
  fullWidth?: boolean;
  lockToOwnOrg?: boolean;
  platformDefaultsToOwnOrg?: boolean;
}) {
  const { userPermissions } = useUser();
  const { selectedOrgId, setSelectedOrgId } = useSelectedOrgStore();
  const ownOrgId = userPermissions?.orgId ?? null;

  const { data: orgList } = useQuery(
    ["org-selector-list"],
    async () => {
      let response = await getOrganizations({ page: 1, size: 1000 });
      return (response?.data?.data?.list || []) as Organization[];
    },
    { enabled: !!userPermissions?.isPlatformOrg }
  );

  useEffect(() => {
    if (!lockToOwnOrg || ownOrgId == null) return;
    // Tenant users: always pinned, even if a stale selection from
    // elsewhere is present. Platform admin: only auto-default to their own
    // org where that's explicitly opted into (create/modify forms) — and
    // even then, only when nothing is picked yet, so an active selection
    // made on this page isn't clobbered.
    if (!userPermissions?.isPlatformOrg) {
      if (selectedOrgId !== ownOrgId) setSelectedOrgId(ownOrgId);
    } else if (platformDefaultsToOwnOrg && selectedOrgId == null) {
      setSelectedOrgId(ownOrgId);
    }
  }, [
    lockToOwnOrg,
    ownOrgId,
    userPermissions?.isPlatformOrg,
    selectedOrgId,
    setSelectedOrgId,
    platformDefaultsToOwnOrg,
  ]);

  if (!userPermissions?.isPlatformOrg) {
    if (!lockToOwnOrg) return null;
    return (
      <TextField
        size="small"
        fullWidth={fullWidth}
        label={label}
        value={userPermissions?.orgName ?? ""}
        disabled
        sx={{
          minWidth: fullWidth ? undefined : { xs: "100%", sm: 220 },
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
      />
    );
  }

  // The Platform org itself is never a meaningful data scope to pick — "All
  // Organisations" already covers that, and listing the Platform org too
  // right next to it would just be two confusingly similar-looking options.
  const tenantOrgs = (orgList || []).filter((org) => !org.isPlatform);

  // "All organisations" is only a real, selectable value where null org is
  // meaningful (list/filter screens) — a create/modify form that requires a
  // concrete org keeps its plain empty-until-picked dropdown.
  const showAllOrgsOption = !platformDefaultsToOwnOrg;
  const options = showAllOrgsOption ? [ALL_ORGS_OPTION, ...tenantOrgs] : tenantOrgs;
  const selectedOrg = selectedOrgId == null
    ? (showAllOrgsOption ? ALL_ORGS_OPTION : null)
    : tenantOrgs.find((org) => org.id === selectedOrgId) || null;

  return (
    <Autocomplete
      size="small"
      fullWidth={fullWidth}
      options={options}
      value={selectedOrg}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, selected) =>
        setSelectedOrgId(selected && selected.id !== ALL_ORGS_OPTION.id ? selected.id : null)
      }
      disableClearable={showAllOrgsOption}
      sx={fullWidth ? undefined : { minWidth: { xs: "100%", sm: 220 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="All organisations"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "&.Mui-focused fieldset": { borderColor: purple },
            },
          }}
        />
      )}
    />
  );
}
